import {
  type HomeworkItem,
  type Submission,
  addDays,
  fetchCourseStudents,
  homeworkToDo,
  shortDate,
  todayIso,
  useCourseHomework,
  useCurrentContext,
  useDb,
  useHomeworkActions,
  useStudentHomework,
  weekStart,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AttachmentList } from '../student/AnswerFiles';
import { useCoursePicker } from '../components/CoursePicker';
import { Badge, Button, EmptyState, Field, Modal, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '../design/icons';
import { buttonClass } from '../design/ui';
import { useLook } from '../design/looks';
import { ShapesHomework } from '../student/ShapesHomework';
import { StudentHomework } from '../student/StudentHomework';
import { weekdayShort } from '../student/home/model';

/*
 * «Домашні завдання» of the family and the teacher in «Форми 2» (28.10). The family sees the child's tasks
 * the way the child does — one block, the subjects as chips, the tasks by when they are due (late, today,
 * tomorrow, this week, later), each a soft card in its subject's gradient with the picture big and quiet
 * behind that opens to read it, the teacher's words and the child's answer; the done ones folded. The
 * teacher sees the course's tasks as a list by due date — a date tile, the task, how many answers came
 * and how many wait to be checked — a tap opens the answers to accept or return.
 */

type Tone = 'neutral' | 'brand' | 'success' | 'danger' | 'shelter';

function statusOf(
  hw: HomeworkItem,
  submission: Submission | undefined,
): { key: string; tone: Tone } {
  if (!hw.requiresSubmission) return { key: 'notRequired', tone: 'neutral' };
  if (!submission) return { key: 'none', tone: hw.dueOn < todayIso() ? 'danger' : 'neutral' };
  return {
    key: submission.status,
    tone:
      submission.status === 'accepted'
        ? 'success'
        : submission.status === 'returned'
          ? 'shelter'
          : 'brand',
  };
}

/** a status's soft mark on a coloured card: white, the words in the status's colour */
const STATUS_INK: Record<Tone, Grad> = {
  neutral: 'stone',
  brand: 'lavender',
  success: 'mint',
  danger: 'coral',
  shelter: 'apricot',
};

const LATE_INK = '#D9471A';

function dueWords(t: TFunction, dueOn: string) {
  const today = todayIso();
  return dueOn === today
    ? t('homework.dueToday')
    : dueOn < today
      ? t('homework.overdue', { date: shortDate(dueOn) })
      : t('homework.due', { date: shortDate(dueOn) });
}

/** A day as a small tile of the paper: the weekday over the date; today in orange. */
function DateTile({ iso }: { iso: string }) {
  const today = iso === todayIso();
  return (
    <span
      className="flex size-11 shrink-0 flex-col items-center justify-center rounded-[12px] leading-none"
      style={
        today
          ? { backgroundImage: grad('orange'), color: '#FFFFFF' }
          : { backgroundColor: PAPER, color: INK }
      }
    >
      <span className="text-[10px] font-medium" style={{ color: today ? '#FFFFFF' : INK_SOFT }}>
        {weekdayShort(iso)}
      </span>
      <span className="mt-0.5 text-[15px] font-medium">{Number(iso.slice(8))}</span>
    </span>
  );
}

/** A group of a list: its name and how many, then the items. */
function Group({
  title,
  count,
  late = false,
  children,
}: {
  title: string;
  count: number;
  late?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mt-6 first:mt-0">
      <h2
        className="mb-2.5 flex items-baseline gap-2 text-[15px] font-medium"
        style={{ color: late ? LATE_INK : INK }}
      >
        {title}
        <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
          {count}
        </span>
      </h2>
      {children}
    </section>
  );
}

const textarea =
  'w-full resize-y rounded-[14px] bg-white p-3.5 text-[15px] leading-6 text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-faint focus:ring-2 focus:ring-[var(--sh-dark)]';

// ---------------------------------------------------------------------------------------------
// Families
// ---------------------------------------------------------------------------------------------

/** One task of the child: a soft card of its subject that opens to read it and the answer. */
function FamilyTask({ hw, submission }: { hw: HomeworkItem; submission: Submission | undefined }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const today = todayIso();
  const status = statusOf(hw, submission);
  const late = status.key === 'none' && hw.dueOn < today;
  const look = subjectLook(hw.subject);
  const tone = SUBJECT_GRAD[look.tone];
  const mark = STATUS_INK[status.tone];
  return (
    <article
      className="relative isolate overflow-hidden rounded-[20px]"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <SubjectArt
        icon={look.icon}
        tone={tone}
        size={92}
        ghost
        className="pointer-events-none absolute -top-3 right-14 -z-10"
      />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span
            className={`block text-[16px] leading-[22px] font-medium ${open ? '' : 'line-clamp-2'}`}
            style={{ color: INK }}
          >
            {hw.title}
          </span>
          <span className="mt-0.5 block text-[13px]">
            {hw.subject}
            {' · '}
            <span style={{ color: late ? LATE_INK : INK_SOFT }}>{dueWords(t, hw.dueOn)}</span>
          </span>
        </span>
        {(status.key !== 'none' || late) && (
          <span
            className="shrink-0 rounded-[7px] bg-white/80 px-2 py-1 text-[12px] leading-none font-medium"
            style={{ color: GRAD[mark].ink }}
          >
            {t(`homework.status.${status.key as 'none'}`)}
          </span>
        )}
        <CaretDownIcon
          size={14}
          weight="bold"
          aria-hidden
          color={INK}
          className={`shrink-0 transition-transform duration-300 ease-spring ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="motion-rise mx-2 mb-2 flex flex-col gap-3 rounded-[16px] bg-white p-4">
          {hw.body && (
            <p className="text-[15px] leading-6" style={{ color: INK }}>
              {hw.body}
            </p>
          )}
          {hw.criteria && (
            <div className="rounded-[16px] p-3.5" style={{ backgroundColor: PAPER }}>
              <p className="text-[12px] font-medium" style={{ color: INK_FAINT }}>
                {t('homework.criteria')}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5 text-[15px]" style={{ color: INK }}>
                {hw.criteria
                  .split('\n')
                  .filter(Boolean)
                  .map((line) => (
                    <li key={line}>• {line}</li>
                  ))}
              </ul>
            </div>
          )}
          {submission?.feedback && (
            <div
              className="rounded-[16px] p-3.5"
              style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
            >
              <p className="text-[12px] font-medium">{t('homework.feedback')}</p>
              <p className="mt-0.5 text-[15px] leading-6" style={{ color: INK }}>
                {submission.feedback}
              </p>
            </div>
          )}
          {submission ? (
            <div className="flex flex-col gap-2">
              <p className="text-[12px] font-medium" style={{ color: INK_FAINT }}>
                {t('homework.answer')}
              </p>
              <p className="text-[15px] leading-6" style={{ color: INK_SOFT }}>
                «{submission.body}»
              </p>
              <AttachmentList paths={submission.attachments} />
            </div>
          ) : (
            hw.requiresSubmission && (
              <p className="text-[13.5px]" style={{ color: INK_SOFT }}>
                {t('homework.onlyStudent')}
              </p>
            )
          )}
        </div>
      )}
    </article>
  );
}

type DueGroup = 'late' | 'today' | 'tomorrow' | 'week' | 'later';
const DUE_GROUPS: ReadonlyArray<DueGroup> = ['late', 'today', 'tomorrow', 'week', 'later'];

function FamilyHomework({ studentId, name }: { studentId: string; name: string }) {
  const { t } = useTranslation();
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(weekStart(today), 6);
  const data = useStudentHomework(studentId, addDays(today, -21));
  const [course, setCourse] = useState<string>();
  const [showDone, setShowDone] = useState(false);
  const homework = data.data?.homework ?? [];
  const submissions = data.data?.submissions ?? [];
  const todo = homeworkToDo(homework, submissions, today, tomorrow);
  const submissionOf = (id: string) => submissions.find((s) => s.homeworkId === id);
  // what is still to do: to hand in and not handed (or returned), or to read and not past
  const toDo = new Set([...todo.overdue, ...todo.open]);
  const active = (h: HomeworkItem) => toDo.has(h) || (!h.requiresSubmission && h.dueOn >= today);
  const subjects = [...new Set(homework.map((h) => h.courseId))]
    .map((courseId) => {
      const all = homework.filter((h) => h.courseId === courseId);
      return {
        courseId,
        subject: (all[0] as HomeworkItem).subject,
        open: all.filter(active).length,
        late: all.filter((h) => todo.overdue.includes(h)).length,
      };
    })
    .sort((a, b) => b.late - a.late || b.open - a.open || a.subject.localeCompare(b.subject, 'uk'));
  const shown = homework.filter((h) => !course || h.courseId === course);
  const groupOf = (h: HomeworkItem): DueGroup =>
    h.dueOn < today
      ? 'late'
      : h.dueOn === today
        ? 'today'
        : h.dueOn === tomorrow
          ? 'tomorrow'
          : h.dueOn <= weekEnd
            ? 'week'
            : 'later';
  const open = shown.filter(active).sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const done = shown.filter((h) => !active(h)).sort((a, b) => b.dueOn.localeCompare(a.dueOn));
  const count = homework.filter(active).length;
  const chip = (on: boolean) =>
    `inline-flex h-10 shrink-0 items-center gap-2 rounded-full pr-3.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;
  const choose = (id: string | undefined) => {
    setShowDone(false);
    setCourse(id);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('homework.title')}
        note={
          count > 0 ? `${name} · ${t('student.homework.active', { count })}` : name || undefined
        }
      />
      <Block>
        {data.isPending ? (
          <div
            className="h-[320px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : data.isError ? (
          <EmptyState>{t('common.error')}</EmptyState>
        ) : homework.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center">
            <TaskArt kind="done" tone="lime" size={96} />
            <p className="mt-3 font-display text-[26px] font-light" style={{ color: INK }}>
              {t('homework.noHomework')}
            </p>
          </div>
        ) : (
          <>
            {/* the subjects as chips: narrow the list to one */}
            <div className="mb-6 flex flex-wrap gap-1.5">
              <button
                type="button"
                aria-pressed={!course}
                onClick={() => choose(undefined)}
                className={`${chip(!course)} pl-4`}
                style={{ backgroundColor: !course ? '#E9E5DC' : PAPER, color: INK }}
              >
                {t('studentHome.shapes2.allSubjects')}
                <span style={{ color: INK_FAINT }}>{count}</span>
              </button>
              {subjects.map((f) => {
                const l = subjectLook(f.subject);
                const on = f.courseId === course;
                return (
                  <button
                    key={f.courseId}
                    type="button"
                    aria-pressed={on}
                    onClick={() => choose(on ? undefined : f.courseId)}
                    className={`${chip(on)} pl-1.5`}
                    style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                  >
                    <SubjectArt icon={l.icon} tone={SUBJECT_GRAD[l.tone]} size={28} />
                    {f.subject}
                    {f.open > 0 && (
                      <span style={{ color: f.late > 0 ? LATE_INK : INK_FAINT }}>{f.open}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {open.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <TaskArt kind="done" tone="lime" size={88} />
                <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
                  {t('studentHome.shapes2.allDone')}
                </p>
              </div>
            ) : (
              <div>
                {DUE_GROUPS.map((g) => {
                  const items = open.filter((h) => groupOf(h) === g);
                  if (items.length === 0) return null;
                  return (
                    <Group
                      key={g}
                      title={t(`studentHome.shapes2.due.${g}`)}
                      count={items.length}
                      late={g === 'late'}
                    >
                      <div className="grid grid-cols-1 items-start gap-2 xl:grid-cols-2">
                        {items.map((hw) => (
                          <FamilyTask key={hw.id} hw={hw} submission={submissionOf(hw.id)} />
                        ))}
                      </div>
                    </Group>
                  );
                })}
              </div>
            )}

            {done.length > 0 && (
              <section className="mt-6 border-t pt-4" style={{ borderColor: LINE }}>
                <button
                  type="button"
                  aria-expanded={showDone}
                  onClick={() => setShowDone(!showDone)}
                  className="flex items-center gap-2 text-[15px] font-medium"
                  style={{ color: INK }}
                >
                  {t('student.homework.done')}
                  <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
                    {done.length}
                  </span>
                  <CaretDownIcon
                    size={12}
                    weight="bold"
                    aria-hidden
                    className={`transition-transform duration-300 ${showDone ? 'rotate-180' : ''}`}
                  />
                </button>
                {showDone && (
                  <div className="mt-3 grid grid-cols-1 items-start gap-2 xl:grid-cols-2">
                    {done.map((hw) => (
                      <FamilyTask key={hw.id} hw={hw} submission={submissionOf(hw.id)} />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </Block>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Teachers and the administration
// ---------------------------------------------------------------------------------------------
function CreateHomework({
  schoolId,
  courseId,
  onClose,
}: {
  schoolId: string;
  courseId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const actions = useHomeworkActions();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [criteria, setCriteria] = useState('');
  const [dueOn, setDueOn] = useState(addDays(todayIso(), 2));
  const [requiresSubmission, setRequiresSubmission] = useState(true);
  return (
    <Modal title={t('homework.create')} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void actions.create
            .mutateAsync({ schoolId, courseId, title, body, criteria, dueOn, requiresSubmission })
            .then(onClose);
        }}
      >
        <Field
          id="hw-title"
          label={t('homework.titleLabel')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-ink-soft">
            {t('homework.bodyLabel')}
          </span>
          <textarea
            className={`${textarea} min-h-24`}
            maxLength={5000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-ink-soft">
            {t('homework.criteriaLabel')}
          </span>
          <textarea
            className={`${textarea} min-h-20`}
            maxLength={1000}
            placeholder={t('homework.criteriaHint')}
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
          />
        </label>
        <Field
          id="hw-due"
          label={t('homework.dueLabel')}
          type="date"
          required
          value={dueOn}
          onChange={(e) => setDueOn(e.target.value)}
        />
        <label
          className="flex cursor-pointer items-center gap-2.5 text-[14px]"
          style={{ color: INK }}
        >
          <input
            type="checkbox"
            checked={requiresSubmission}
            onChange={(e) => setRequiresSubmission(e.target.checked)}
            className="size-[18px] accent-[var(--sh-dark)]"
          />
          {t('homework.requiresSubmission')}
        </label>
        <div>
          <Button
            type="submit"
            className="!w-auto px-6"
            disabled={!title.trim() || actions.create.isPending}
          >
            {t('homework.publish')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ReviewModal({
  hw,
  courseId,
  submissions,
  onClose,
}: {
  hw: HomeworkItem;
  courseId: string;
  submissions: Submission[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const db = useDb();
  const actions = useHomeworkActions();
  const students = useQuery({
    queryKey: ['course-students', courseId],
    queryFn: () => fetchCourseStudents(db, courseId),
  });
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const handedIn = (students.data ?? []).filter((s) =>
    submissions.some((x) => x.studentId === s.id),
  );
  const missing = (students.data ?? []).filter(
    (s) => !submissions.some((x) => x.studentId === s.id),
  );
  const late = hw.dueOn < todayIso();
  return (
    <Modal title={`${hw.subject} · ${hw.title}`} onClose={onClose}>
      <p className="-mt-2 mb-4 text-[14px]" style={{ color: INK_SOFT }}>
        {dueWords(t, hw.dueOn)}
      </p>
      {students.isPending ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-2">
          {handedIn.map((s) => {
            const sub = submissions.find((x) => x.studentId === s.id) as Submission;
            const status = statusOf(hw, sub);
            return (
              <div key={s.id} className="rounded-[20px] p-4" style={{ backgroundColor: PAPER }}>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="text-[15px] font-medium" style={{ color: INK }}>
                    {s.last_name} {s.first_name}
                  </span>
                  <Badge tone={status.tone}>{t(`homework.status.${status.key as 'none'}`)}</Badge>
                </div>
                <p className="mb-2 text-[15px] leading-6" style={{ color: INK }}>
                  {sub.body}
                </p>
                <div className="mb-2">
                  <AttachmentList paths={sub.attachments} />
                </div>
                <input
                  className="mb-2.5 h-10 w-full rounded-[12px] bg-white px-3 text-[14px] text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-faint focus:ring-2 focus:ring-[var(--sh-dark)]"
                  placeholder={t('homework.feedbackPlaceholder')}
                  value={feedback[sub.id] ?? sub.feedback ?? ''}
                  onChange={(e) => setFeedback({ ...feedback, [sub.id]: e.target.value })}
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={buttonClass('primary', 'sm')}
                    onClick={() =>
                      actions.review.mutate({
                        id: sub.id,
                        status: 'accepted',
                        feedback: feedback[sub.id] ?? sub.feedback ?? '',
                      })
                    }
                  >
                    {t('homework.accept')}
                  </button>
                  <button
                    type="button"
                    className={buttonClass('outline', 'sm')}
                    onClick={() =>
                      actions.review.mutate({
                        id: sub.id,
                        status: 'returned',
                        feedback: feedback[sub.id] ?? sub.feedback ?? '',
                      })
                    }
                  >
                    {t('homework.return')}
                  </button>
                </div>
              </div>
            );
          })}
          {missing.length > 0 && (
            <div className="mt-2">
              <p
                className="mb-1 text-[13px] font-medium"
                style={{ color: late ? LATE_INK : INK_SOFT }}
              >
                {t('homework.notHandedIn')} · {missing.length}
              </p>
              <p className="text-[14px] leading-6" style={{ color: INK }}>
                {missing.map((s) => `${s.last_name} ${s.first_name}`).join(', ')}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** One task of the course: the due date as a tile, the task, the answers; opens them to check. */
function TeacherRow({
  hw,
  submissions,
  onOpen,
}: {
  hw: HomeworkItem;
  submissions: Submission[];
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const toReview = submissions.filter((s) => s.status === 'submitted').length;
  const today = todayIso();
  return (
    <li className="border-t first:border-t-0" style={{ borderColor: LINE }}>
      <button
        type="button"
        onClick={onOpen}
        className="group -mx-2 flex w-[calc(100%+16px)] flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[16px] px-2 py-2.5 text-left transition-colors hover:bg-[#FAF9F6]"
      >
        <DateTile iso={hw.dueOn} />
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[15.5px] leading-[22px] font-medium"
            style={{ color: INK }}
          >
            {hw.title}
          </span>
          <span
            className="block text-[12.5px]"
            style={{ color: hw.dueOn === today ? LATE_INK : INK_SOFT }}
          >
            {dueWords(t, hw.dueOn)}
          </span>
        </span>
        {/* on a phone the answers go under the task, at the right on a wide screen */}
        <span className="order-last flex basis-full flex-wrap gap-1 pl-14 sm:order-none sm:basis-auto sm:justify-end sm:pl-0">
          {hw.requiresSubmission ? (
            <>
              <span
                className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                {t('homework.answers', { count: submissions.length })}
              </span>
              {toReview > 0 && (
                <Badge tone="brand">{t('homework.toReview', { count: toReview })}</Badge>
              )}
            </>
          ) : (
            <Badge>{t('homework.status.notRequired')}</Badge>
          )}
        </span>
        <CaretRightIcon
          size={13}
          weight="bold"
          aria-hidden
          className="shrink-0 text-[var(--sh-ink-faint)] transition-colors group-hover:text-[var(--sh-ink)]"
        />
      </button>
    </li>
  );
}

function TeacherHomework({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const picker = useCoursePicker();
  const data = useCourseHomework(picker.course?.id);
  const [creating, setCreating] = useState(false);
  const [reviewing, setReviewing] = useState<string>();
  const [allPast, setAllPast] = useState(false);
  const reviewed = data.data?.homework.find((h) => h.id === reviewing);
  const today = todayIso();
  const all = data.data?.homework ?? [];
  const subs = data.data?.submissions ?? [];
  const subsOf = (id: string) => subs.filter((s) => s.homeworkId === id);
  const upcoming = all
    .filter((h) => h.dueOn >= today)
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const past = all.filter((h) => h.dueOn < today).sort((a, b) => b.dueOn.localeCompare(a.dueOn));
  // the past ones that still wait to be checked come first and are never folded
  const waiting = (h: HomeworkItem) => subsOf(h.id).some((s) => s.status === 'submitted');
  const pastSorted = [...past.filter(waiting), ...past.filter((h) => !waiting(h))];
  const pastShown = allPast
    ? pastSorted
    : pastSorted.slice(0, Math.max(5, past.filter(waiting).length));
  const toReview = subs.filter((s) => s.status === 'submitted').length;
  const course = picker.course;
  const look = course ? subjectLook(course.subject) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('homework.title')}
        note={toReview > 0 ? t('homework.toReview', { count: toReview }) : undefined}
      >
        {/* bounded, so on a phone the choices wrap under the title instead of widening the page */}
        <div className="flex max-w-[calc(100vw-32px)] flex-wrap items-center gap-2">
          {picker.controls}
          {course && (
            <button type="button" className={pillClass('dark')} onClick={() => setCreating(true)}>
              {t('homework.create')}
              <PillIcon icon={PlusIcon} />
            </button>
          )}
        </div>
      </PageHead>
      {picker.loading || data.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <Block>
          {course && look && (
            <header className="mb-5 flex items-center gap-3">
              <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
              <p
                className="min-w-0 truncate font-display text-[24px] leading-7 font-light tracking-[-0.03em]"
                style={{ color: INK }}
              >
                {course.subject}
                {course.groupLabel ? ` (${course.groupLabel})` : ''}{' '}
                <span style={{ color: INK_FAINT }}>{course.classLabel}</span>
              </p>
            </header>
          )}
          {all.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <TaskArt kind="done" tone="lime" size={88} />
              <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
                {t('homework.noHomework')}
              </p>
            </div>
          ) : (
            <div>
              {upcoming.length > 0 && (
                <Group title={t('homework.upcoming')} count={upcoming.length}>
                  <ul className="flex flex-col">
                    {upcoming.map((hw) => (
                      <TeacherRow
                        key={hw.id}
                        hw={hw}
                        submissions={subsOf(hw.id)}
                        onOpen={() => setReviewing(hw.id)}
                      />
                    ))}
                  </ul>
                </Group>
              )}
              {past.length > 0 && (
                <Group title={t('homework.past')} count={past.length}>
                  <ul className="flex flex-col">
                    {pastShown.map((hw) => (
                      <TeacherRow
                        key={hw.id}
                        hw={hw}
                        submissions={subsOf(hw.id)}
                        onOpen={() => setReviewing(hw.id)}
                      />
                    ))}
                  </ul>
                  {pastShown.length < past.length && (
                    <button
                      type="button"
                      onClick={() => setAllPast(true)}
                      className="mt-2 inline-flex h-9 items-center rounded-full px-4 text-[13px] font-medium transition-colors hover:bg-[#EFECE5]"
                      style={{ backgroundColor: PAPER, color: INK }}
                    >
                      {t('studentHome.shapes2.moreTasks', {
                        count: past.length - pastShown.length,
                      })}
                    </button>
                  )}
                </Group>
              )}
            </div>
          )}
        </Block>
      )}
      {creating && course && (
        <CreateHomework
          schoolId={schoolId}
          courseId={course.id}
          onClose={() => setCreating(false)}
        />
      )}
      {reviewed && course && (
        <ReviewModal
          hw={reviewed}
          courseId={course.id}
          submissions={subsOf(reviewed.id)}
          onClose={() => setReviewing(undefined)}
        />
      )}
    </div>
  );
}

export function HomeworkPage() {
  const ctx = useCurrentContext();
  const look = useLook();
  if (ctx.kind === 'student')
    return look === 'shapes2' ? (
      <ShapesHomework key={ctx.key} ctx={ctx} />
    ) : (
      <StudentHomework key={ctx.key} ctx={ctx} />
    );
  if (ctx.kind === 'guardian')
    return (
      <FamilyHomework key={ctx.key} studentId={ctx.child.memberId} name={ctx.child.firstName} />
    );
  return <TeacherHomework schoolId={ctx.schoolId} />;
}
